import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { SIMULATION } from '../config/simulatorConfig';
import BUSINESS_CONFIG from '../config/businessConfig';

// Initial state
const initialState = {
  // Simulation state
  currentWeek: 1,
  isComplete: false,
  showLimitSetup: true, // Show limit setup by default
  
  // Week history tracking
  weekHistory: [], // Array to store weekly statistics
  
  // Financial state
  balance: SIMULATION.STARTING_BALANCE,
  totalDeposited: 0,
  totalWithdrawn: 0,
  depositedToday: 0,
  depositedThisWeek: 0,
  depositedThisMonth: 0,
  withdrawnToday: 0,
  withdrawnThisWeek: 0,
  withdrawnThisMonth: 0,
  netResult: 0, 
  
  // Time tracking
  totalPlayTime: 0,
  weeklyPlayTime: 0,
  playTimeToday: 0,
  
  // Limits state
  limits: {
    netDeposit: BUSINESS_CONFIG.limits.netDeposit.default,
    deposit: {
      daily: BUSINESS_CONFIG.limits.deposit.daily.default,
      weekly: BUSINESS_CONFIG.limits.deposit.weekly.default,
      monthly: BUSINESS_CONFIG.limits.deposit.monthly.default,
    },
    login: {
      daily: BUSINESS_CONFIG.limits.login.daily.default,
      weekly: BUSINESS_CONFIG.limits.login.weekly.default,
      monthly: BUSINESS_CONFIG.limits.login.monthly.default,
    },
    withdrawal: {
      maxBalance: BUSINESS_CONFIG.limits.withdrawal.maxBalance.default,
      reserve: BUSINESS_CONFIG.limits.withdrawal.reserve.default,
    },
  },
  
  // Flag to track if limits have been explicitly configured by user
  limitsConfigured: false,
  
  // Limit tracking
  limitStatus: {
    netDepositReached: false,
    depositReached: {
      daily: false,
      weekly: false,
      monthly: false,
    },
    loginReached: {
      daily: false,
      weekly: false,
      monthly: false,
    },
    withdrawalTriggered: false,
  },
  
  // Analytics events
  analyticsEvents: [],
};

// Action types
const ACTIONS = {
  SET_WEEK: 'SET_WEEK',
  NEXT_WEEK: 'NEXT_WEEK',
  DEPOSIT: 'DEPOSIT',
  WITHDRAW: 'WITHDRAW',
  PLAY: 'PLAY',
  RESET: 'RESET',
  SET_LIMITS: 'SET_LIMITS',
  SET_LIMIT_SETUP: 'SET_LIMIT_SETUP',
  SET_LIMITS_CONFIGURED: 'SET_LIMITS_CONFIGURED',
  SET_CURRENT_WEEK: 'SET_CURRENT_WEEK',
  SET_IS_COMPLETE: 'SET_IS_COMPLETE',
  SET_WEEK_HISTORY: 'SET_WEEK_HISTORY',
  SET_BALANCE: 'SET_BALANCE',
  SET_TOTAL_DEPOSITED: 'SET_TOTAL_DEPOSITED',
  SET_TOTAL_WITHDRAWN: 'SET_TOTAL_WITHDRAWN',
  SET_DEPOSITED_TODAY: 'SET_DEPOSITED_TODAY',
  SET_DEPOSITED_THIS_WEEK: 'SET_DEPOSITED_THIS_WEEK',
  SET_DEPOSITED_THIS_MONTH: 'SET_DEPOSITED_THIS_MONTH',
  SET_WITHDRAWN_TODAY: 'SET_WITHDRAWN_TODAY',
  SET_WITHDRAWN_THIS_WEEK: 'SET_WITHDRAWN_THIS_WEEK',
  SET_WITHDRAWN_THIS_MONTH: 'SET_WITHDRAWN_THIS_MONTH',
  SET_NET_RESULT: 'SET_NET_RESULT',
  SET_TOTAL_PLAY_TIME: 'SET_TOTAL_PLAY_TIME',
  SET_WEEKLY_PLAY_TIME: 'SET_WEEKLY_PLAY_TIME',
  SET_PLAY_TIME_TODAY: 'SET_PLAY_TIME_TODAY',
  SET_LIMIT_STATUS: 'SET_LIMIT_STATUS',
  LOG_ANALYTICS: 'LOG_ANALYTICS',
};

// Reducer function
function simulatorReducer(state, action) {
  switch (action.type) {
    case ACTIONS.NEXT_WEEK:
      // Save current week's data to history
      const weekData = {
        week: state.currentWeek,
        deposited: state.depositedThisWeek,
        withdrawn: state.withdrawnThisWeek,
        playTime: state.weeklyPlayTime,
        netResult: state.netResult,
        finalBalance: state.balance
      };

      // Reset daily and weekly counters, but keep monthly and total
      return {
        ...state,
        currentWeek: state.currentWeek + 1,
        isComplete: state.currentWeek + 1 > SIMULATION.WEEKS,
        weekHistory: [...state.weekHistory, weekData],
        
        // Reset weekly and daily counters
        depositedToday: 0,
        depositedThisWeek: 0,
        withdrawnToday: 0,
        withdrawnThisWeek: 0,
        weeklyPlayTime: 0,
        playTimeToday: 0,
        netResult: 0,
        
        // Keep monthly and total counters
        totalDeposited: state.totalDeposited,
        totalWithdrawn: state.totalWithdrawn,
        totalPlayTime: state.totalPlayTime,
        depositedThisMonth: state.depositedThisMonth,
        withdrawnThisMonth: state.withdrawnThisMonth,
        
        // Keep balance
        balance: state.balance,
        
        // Reset limit status for daily and weekly
        limitStatus: {
          ...state.limitStatus,
          depositReached: {
            ...state.limitStatus.depositReached,
            daily: false,
            weekly: false,
          },
          loginReached: {
            ...state.limitStatus.loginReached,
            daily: false,
            weekly: false,
          }
        }
      };

    case ACTIONS.SET_WEEK:
      return {
        ...state,
        currentWeek: action.payload,
        isComplete: action.payload > SIMULATION.WEEKS,
        weeklyPlayTime: 0,
        playTimeToday: 0,
      };

    case ACTIONS.DEPOSIT:
      const { amount } = action.payload;
      const newTotalDeposited = state.totalDeposited + amount;
      let newBalance = state.balance + amount;
      const newDepositedToday = state.depositedToday + amount;
      const newDepositedThisWeek = state.depositedThisWeek + amount;
      const newDepositedThisMonth = state.depositedThisMonth + amount;
      
      // Check if any deposit limits are reached
      const depositReached = {
        ...state.limitStatus.depositReached,
        monthly: newDepositedThisMonth >= state.limits.deposit.monthly,
        weekly: newDepositedThisWeek >= state.limits.deposit.weekly,
        daily: newDepositedToday >= state.limits.deposit.daily,
      };
      
      // Always recalculate net deposit limit
      const netDepositReached = (newTotalDeposited - state.totalWithdrawn) >= state.limits.netDeposit;

      // Automatic withdrawal logic (deposit)
      let withdrawalTriggeredDeposit = false;
      let autoWithdrawnDeposit = 0;
      if (newBalance > state.limits.withdrawal.maxBalance) {
        withdrawalTriggeredDeposit = true;
        autoWithdrawnDeposit = newBalance - state.limits.withdrawal.reserve;
        newBalance = state.limits.withdrawal.reserve;
      }

      return {
        ...state,
        balance: newBalance,
        totalDeposited: newTotalDeposited,
        depositedToday: newDepositedToday,
        depositedThisWeek: newDepositedThisWeek,
        depositedThisMonth: newDepositedThisMonth,
        totalWithdrawn: state.totalWithdrawn + autoWithdrawnDeposit,
        withdrawnToday: state.withdrawnToday + autoWithdrawnDeposit,
        withdrawnThisWeek: state.withdrawnThisWeek + autoWithdrawnDeposit,
        withdrawnThisMonth: state.withdrawnThisMonth + autoWithdrawnDeposit,
        limitStatus: {
          ...state.limitStatus,
          depositReached,
          netDepositReached,
          withdrawalTriggered: withdrawalTriggeredDeposit,
        },
      };

    case ACTIONS.WITHDRAW:
      const withdrawAmount = action.payload.amount;
      let newWithdrawnBalance = state.balance - withdrawAmount;
      const newWithdrawnToday = state.withdrawnToday + withdrawAmount;
      const newWithdrawnThisWeek = state.withdrawnThisWeek + withdrawAmount;
      const newWithdrawnThisMonth = state.withdrawnThisMonth + withdrawAmount;
      
      // Automatic withdrawal logic (withdraw)
      let withdrawalTriggeredWithdraw = false;
      let autoWithdrawnWithdraw = 0;
      if (newWithdrawnBalance > state.limits.withdrawal.maxBalance) {
        withdrawalTriggeredWithdraw = true;
        autoWithdrawnWithdraw = newWithdrawnBalance - state.limits.withdrawal.reserve;
        newWithdrawnBalance = state.limits.withdrawal.reserve;
      }

      // Always recalculate net deposit limit after withdrawal
      const netDepositReachedWithdraw = (state.totalDeposited - (state.totalWithdrawn + withdrawAmount + autoWithdrawnWithdraw)) >= state.limits.netDeposit;

      return {
        ...state,
        balance: newWithdrawnBalance,
        totalWithdrawn: state.totalWithdrawn + withdrawAmount + autoWithdrawnWithdraw,
        withdrawnToday: newWithdrawnToday + autoWithdrawnWithdraw,
        withdrawnThisWeek: newWithdrawnThisWeek + autoWithdrawnWithdraw,
        withdrawnThisMonth: newWithdrawnThisMonth + autoWithdrawnWithdraw,
        limitStatus: {
          ...state.limitStatus,
          withdrawalTriggered: withdrawalTriggeredWithdraw,
          netDepositReached: netDepositReachedWithdraw,
        },
      };

    case ACTIONS.PLAY:
      const { duration, betAmount, isWin } = action.payload;
      // If win, add betAmount * multiplier to balance, if lose subtract betAmount
      const winAmount = isWin ? betAmount * SIMULATION.WIN_MULTIPLIER : -betAmount;
      const newPlayBalance = state.balance + winAmount;
      const newTotalPlayTime = state.totalPlayTime + duration;
      const newWeeklyPlayTime = state.weeklyPlayTime + duration;
      const newPlayTimeToday = state.playTimeToday + duration;
      // Update net result with the actual win/loss amount
      const newNetResult = state.netResult + winAmount;

      // Check if any login limits are reached
      const loginReached = {
        ...state.limitStatus.loginReached,
        daily: newPlayTimeToday >= state.limits.login.daily,
        weekly: newWeeklyPlayTime >= state.limits.login.weekly,
        monthly: newTotalPlayTime >= state.limits.login.monthly,
      };

      return {
        ...state,
        balance: newPlayBalance,
        totalPlayTime: newTotalPlayTime,
        weeklyPlayTime: newWeeklyPlayTime,
        playTimeToday: newPlayTimeToday,
        netResult: newNetResult,
        limitStatus: {
          ...state.limitStatus,
          loginReached,
        },
      };

    case ACTIONS.RESET:
      if (action.payload?.withNewLimits) {
        // Reset everything including limits to defaults
        return {
          ...initialState,
          showLimitSetup: true, // Show limit setup screen
          limitsConfigured: false, // Reset the configured flag
        };
      } else {
        // Reset simulation state but keep current limits
        return {
          ...initialState,
          showLimitSetup: false, // Ensure we go to simulation, not limit setup
          limits: state.limits, // Keep the limits configuration
          limitsConfigured: state.limitsConfigured, // Keep the configured flag
        };
      }

    case ACTIONS.SET_LIMITS:
      return {
        ...state,
        limits: {
          ...state.limits,
          ...action.payload,
        },
      };

    case ACTIONS.SET_LIMIT_SETUP:
      return {
        ...state,
        showLimitSetup: action.payload,
      };

    case ACTIONS.SET_LIMITS_CONFIGURED:
      return {
        ...state,
        limitsConfigured: action.payload,
      };

    case ACTIONS.SET_CURRENT_WEEK:
      return {
        ...state,
        currentWeek: action.payload,
      };

    case ACTIONS.SET_IS_COMPLETE:
      return {
        ...state,
        isComplete: action.payload,
      };

    case ACTIONS.SET_WEEK_HISTORY:
      return {
        ...state,
        weekHistory: action.payload,
      };

    case ACTIONS.SET_BALANCE:
      return {
        ...state,
        balance: action.payload,
      };

    case ACTIONS.SET_TOTAL_DEPOSITED:
      return {
        ...state,
        totalDeposited: action.payload,
      };

    case ACTIONS.SET_TOTAL_WITHDRAWN:
      return {
        ...state,
        totalWithdrawn: action.payload,
      };

    case ACTIONS.SET_DEPOSITED_TODAY:
      return {
        ...state,
        depositedToday: action.payload,
      };

    case ACTIONS.SET_DEPOSITED_THIS_WEEK:
      return {
        ...state,
        depositedThisWeek: action.payload,
      };

    case ACTIONS.SET_DEPOSITED_THIS_MONTH:
      return {
        ...state,
        depositedThisMonth: action.payload,
      };

    case ACTIONS.SET_WITHDRAWN_TODAY:
      return {
        ...state,
        withdrawnToday: action.payload,
      };

    case ACTIONS.SET_WITHDRAWN_THIS_WEEK:
      return {
        ...state,
        withdrawnThisWeek: action.payload,
      };

    case ACTIONS.SET_WITHDRAWN_THIS_MONTH:
      return {
        ...state,
        withdrawnThisMonth: action.payload,
      };

    case ACTIONS.SET_NET_RESULT:
      return {
        ...state,
        netResult: action.payload,
      };

    case ACTIONS.SET_TOTAL_PLAY_TIME:
      return {
        ...state,
        totalPlayTime: action.payload,
      };

    case ACTIONS.SET_WEEKLY_PLAY_TIME:
      return {
        ...state,
        weeklyPlayTime: action.payload,
      };

    case ACTIONS.SET_PLAY_TIME_TODAY:
      return {
        ...state,
        playTimeToday: action.payload,
      };

    case ACTIONS.SET_LIMIT_STATUS:
      return {
        ...state,
        limitStatus: action.payload,
      };

    case ACTIONS.LOG_ANALYTICS:
      return {
        ...state,
        analyticsEvents: [
          ...state.analyticsEvents,
          {
            timestamp: new Date().toISOString(),
            ...action.payload,
          },
        ],
      };

    default:
      return state;
  }
}

// Create context
const SimulatorContext = createContext();

// Provider component
export function SimulatorProvider({ children }) {
  const [state, dispatch] = useReducer(simulatorReducer, initialState);

  // Helper function to check if limits are configured
  const areLimitsConfigured = (limits) => {
    return limits && 
           limits.netDeposit > 0 &&
           limits.deposit.daily > 0 &&
           limits.deposit.weekly > 0 &&
           limits.deposit.monthly > 0 &&
           limits.login.daily > 0 &&
           limits.login.weekly > 0 &&
           limits.login.monthly > 0 &&
           limits.withdrawal.maxBalance > 0 &&
           limits.withdrawal.reserve > 0;
  };

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('simulatorState');
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      // We don't want to restore analytics events
      const { analyticsEvents, ...restoredState } = parsedState;
      
      // Determine if we should show limit setup based on whether limits have been configured
      const shouldShowLimitSetup = !restoredState.limitsConfigured;
      
      // Restore state using proper action types
      Object.entries(restoredState).forEach(([key, value]) => {
        const actionType = `SET_${key.toUpperCase()}`;
        if (Object.values(ACTIONS).includes(actionType)) {
          dispatch({ type: actionType, payload: value });
        }
      });
      
      // Set the correct showLimitSetup value
      dispatch({ type: ACTIONS.SET_LIMIT_SETUP, payload: shouldShowLimitSetup });
    }
  }, []);

  // Save state to localStorage on changes
  useEffect(() => {
    const stateToSave = { ...state };
    // Don't save analytics events
    delete stateToSave.analyticsEvents;
    localStorage.setItem('simulatorState', JSON.stringify(stateToSave));
  }, [state]);

  // Context value
  const value = {
    state,
    actions: {
      setWeek: (week) => dispatch({ type: ACTIONS.SET_WEEK, payload: week }),
      nextWeek: () => dispatch({ type: ACTIONS.NEXT_WEEK }),
      deposit: (amount) => dispatch({ type: ACTIONS.DEPOSIT, payload: { amount } }),
      withdraw: (amount) => dispatch({ type: ACTIONS.WITHDRAW, payload: { amount } }),
      play: (duration, betAmount, isWin) => dispatch({ type: ACTIONS.PLAY, payload: { duration, betAmount, isWin } }),
      reset: (payload) => dispatch({ type: ACTIONS.RESET, payload }),
      setLimits: (limits) => dispatch({ type: ACTIONS.SET_LIMITS, payload: limits }),
      setLimitSetup: (show) => dispatch({ type: ACTIONS.SET_LIMIT_SETUP, payload: show }),
      setLimitsConfigured: (configured) => dispatch({ type: ACTIONS.SET_LIMITS_CONFIGURED, payload: configured }),
      logAnalytics: (event) => dispatch({ type: ACTIONS.LOG_ANALYTICS, payload: event }),
    },
  };

  return (
    <SimulatorContext.Provider value={value}>
      {children}
    </SimulatorContext.Provider>
  );
}

// Custom hook to use the simulator context
export function useSimulator() {
  const context = useContext(SimulatorContext);
  if (context === undefined) {
    throw new Error('useSimulator must be used within a SimulatorProvider');
  }
  return context;
} 